import React, { useState } from 'react';
import { X, Loader2, CheckCircle, AlertTriangle, ChevronDown } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { useConnectAdCredentials } from '../../hooks/useConnectAdCredentials';
import { functions } from '../../lib/firebase';

interface Props {
  companyId: string | undefined;
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
}

// State token returned by prepareGoogleAdsOAuth is server-validated.
// Frontend stores ONLY the state string in sessionStorage so the
// callback handler can verify the state matches what was issued.
// All sensitive context (customerIds, loginCustomerId, companyId) is
// persisted server-side keyed by state. Eliminates the CodeQL
// clear-text-storage finding and improves robustness.
const GOOGLE_ADS_STATE_STORAGE_KEY = 'googleads-oauth-state';

const GOOGLE_OAUTH_AUTHORIZE_URL =
  'https://accounts.google.com/o/oauth2/v2/auth';
const GOOGLE_ADS_OAUTH_SCOPE = 'https://www.googleapis.com/auth/adwords';

function buildGoogleAdsAuthUrl(state: string, redirectUri: string): string {
  const clientId = process.env.REACT_APP_GOOGLE_ADS_OAUTH_CLIENT_ID || '';
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: GOOGLE_ADS_OAUTH_SCOPE,
    // access_type=offline + prompt=consent are both required for Google
    // to issue a refresh_token. Without prompt=consent, Google omits the
    // refresh_token on subsequent re-auths and the backend rejects with
    // "did not return a refresh_token".
    access_type: 'offline',
    prompt: 'consent',
    include_granted_scopes: 'true',
    state,
  });
  return `${GOOGLE_OAUTH_AUTHORIZE_URL}?${params.toString()}`;
}

export const ConnectGoogleAdsModal: React.FC<Props> = ({
  companyId,
  open,
  onClose,
  onConnected,
}) => {
  const { pasteGoogleAds } = useConnectAdCredentials(companyId);
  const [developerToken, setDeveloperToken] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [customerIds, setCustomerIds] = useState('');
  const [loginCustomerId, setLoginCustomerId] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [showPasteCredsForm, setShowPasteCredsForm] = useState(false);

  if (!open) return null;

  const handleConnectWithGoogle = async () => {
    if (!companyId) {
      setError('Company ID not loaded yet — try again in a moment.');
      return;
    }
    setError(null);
    setSaving(true);

    try {
      // Persist OAuth context server-side. Backend issues a state
      // token bound to the current admin's auth + company. Frontend
      // never stores customer IDs in sessionStorage.
      //
      // We pass empty customerIds + null loginCustomerId — the
      // completeGoogleAdsOAuth handler discovers accessible customers
      // via Google Ads `customers:listAccessibleCustomers` after the
      // token exchange, so the user does NOT need to know their
      // 10-digit customer ID up-front. This matches the "OAuth — no
      // API keys to copy" promise. Tenants who want to restrict to a
      // subset of their accounts can manage that post-connection.
      const prepareFn = httpsCallable<
        {
          action: 'prepareGoogleAdsOAuth';
          companyId: string;
          customerIds: string[];
          loginCustomerId: string | null;
        },
        { success: boolean; state: string }
      >(functions, 'connectAdCredentials');
      const result = await prepareFn({
        action: 'prepareGoogleAdsOAuth',
        companyId,
        customerIds: [],
        loginCustomerId: null,
      });
      const stateValue = result.data.state;
      if (!stateValue) {
        throw new Error('Backend did not return a state token.');
      }

      const redirectUri = `${window.location.origin}/leads/setup`;
      // Only the state token + companyId hint go to sessionStorage.
      // companyId here is the user's own company ID — not sensitive
      // (it's already in URL routes throughout the app). State is
      // single-use and short-lived; even if exfiltrated by XSS it
      // cannot be replayed.
      try {
        sessionStorage.setItem(
          GOOGLE_ADS_STATE_STORAGE_KEY,
          JSON.stringify({ state: stateValue, companyId, redirectUri })
        );
      } catch (err) {
        console.warn('[ConnectGoogleAdsModal] sessionStorage unavailable', err);
      }
      const authUrl = buildGoogleAdsAuthUrl(stateValue, redirectUri);
      // Google's consent flow doesn't reliably support window.open popups
      // — some browsers / corporate policies block them. Top-level
      // navigate is the supported pattern.
      window.location.assign(authUrl);
    } catch (err) {
      setError(
        err instanceof Error
          ? `Could not start Google connection: ${err.message}`
          : 'Could not start Google connection.'
      );
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    const parsedCustomerIds = customerIds
      .split(/[\n,]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (
      !developerToken.trim() ||
      !clientId.trim() ||
      !clientSecret.trim() ||
      !refreshToken.trim() ||
      parsedCustomerIds.length === 0
    ) {
      setError('All fields are required. Customer IDs — one per line.');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await pasteGoogleAds({
        developerToken: developerToken.trim(),
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        refreshToken: refreshToken.trim(),
        customerIds: parsedCustomerIds,
        loginCustomerId: loginCustomerId.trim() || undefined,
      });
      setConnected(true);
      onConnected?.();
      setTimeout(() => {
        onClose();
        setConnected(false);
        setDeveloperToken('');
        setClientId('');
        setClientSecret('');
        setRefreshToken('');
        setCustomerIds('');
        setLoginCustomerId('');
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700 sticky top-0 bg-gray-900 z-10">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 rounded bg-[#4285f4] text-white inline-flex items-center justify-center font-bold text-sm">
              G
            </span>
            <h2 className="text-base font-semibold text-white">
              Connect Google Ads
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          {/* PRIMARY OAuth path */}
          <div className="space-y-3">
            <div className="text-sm text-gray-300">
              Sign in with your Google Ads account. Valutten reads campaign
              performance, spend, and lead form submissions — no write access
              requested.
            </div>
            <div className="text-xs text-gray-500">
              We&apos;ll discover the Google Ads accounts you have access to
              automatically after sign-in — no Customer IDs to look up.
            </div>
            {error && !showPasteCredsForm && (
              <div className="rounded-md border border-red-500/30 bg-red-500/5 p-2 text-xs text-red-300">
                {error}
              </div>
            )}
            <button
              type="button"
              onClick={handleConnectWithGoogle}
              disabled={saving || !companyId}
              className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#4285f4] hover:bg-[#357ae8] text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <span className="w-5 h-5 rounded bg-white text-[#4285f4] inline-flex items-center justify-center font-bold text-xs">
                  G
                </span>
              )}
              {saving ? 'Redirecting to Google…' : 'Connect with Google'}
            </button>
          </div>

          {/* Disclosure: paste-credentials fallback */}
          <button
            type="button"
            onClick={() => setShowPasteCredsForm((v) => !v)}
            className="w-full flex items-center justify-between text-left text-xs text-gray-400 hover:text-gray-200 px-3 py-2 rounded-md border border-gray-800 hover:border-gray-700"
          >
            <span>
              Or paste credentials manually
              <span className="text-gray-500 ml-1">
                (developer token + refresh token)
              </span>
            </span>
            <ChevronDown
              className={`w-4 h-4 transition-transform ${
                showPasteCredsForm ? 'rotate-180' : ''
              }`}
            />
          </button>

          {!showPasteCredsForm ? null : (
          <div className="space-y-3 pt-2">
          <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-300 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              For brokers with their own Google Ads developer app — paste
              credentials from the Google Ads API Center + an OAuth refresh
              token. Most users should use the Connect with Google button
              above instead.
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Developer token
            </label>
            <input
              type="password"
              value={developerToken}
              onChange={(e) => setDeveloperToken(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                OAuth client ID
              </label>
              <input
                type="text"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-400 mb-1">
                OAuth client secret
              </label>
              <input
                type="password"
                value={clientSecret}
                onChange={(e) => setClientSecret(e.target.value)}
                className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Refresh token
            </label>
            <input
              type="password"
              value={refreshToken}
              onChange={(e) => setRefreshToken(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Customer IDs (one per line — 10 digits, no dashes)
            </label>
            <textarea
              value={customerIds}
              onChange={(e) => setCustomerIds(e.target.value)}
              placeholder={'1234567890\n9876543210'}
              rows={3}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs resize-none"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Login customer ID (MCC — optional)
            </label>
            <input
              type="text"
              value={loginCustomerId}
              onChange={(e) => setLoginCustomerId(e.target.value)}
              placeholder="1234567890"
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          {error && showPasteCredsForm && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
              {error}
            </div>
          )}
          {connected && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Google Ads credentials stored.
            </div>
          )}
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={saving || connected}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
            >
              {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              {saving ? 'Connecting…' : 'Save pasted credentials'}
            </button>
          </div>
          </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-700 sticky bottom-0 bg-gray-900 z-10">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectGoogleAdsModal;
